import { useEffect, useRef, useState } from "react";
import type { AdminCleaner } from "@/mocks/admin";

type Props = {
  open: boolean;
  onClose: () => void;
  onAdd: (cleaner: AdminCleaner) => void;
};

const roleOptions = [
  "Standard Cleaner",
  "Senior Cleaner",
  "Deep Clean Specialist",
  "Eco Clean Lead",
  "Airbnb Turnover Pro",
  "Cleaner",
];

const cityOptions = ["Manhattan", "Brooklyn", "Queens"];

const statusOptions: { id: AdminCleaner["status"]; label: string; hint: string }[] = [
  { id: "onboarding", label: "Onboarding", hint: "Training in progress" },
  { id: "active", label: "Active", hint: "Ready for bookings" },
  { id: "on_leave", label: "On leave", hint: "Temporarily paused" },
];

const placeholderImages = [
  "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20friendly%20woman%20cleaner%20warm%20smile%20cream%20apron%20clean%20beige%20background%20editorial%20natural%20soft%20light%20approachable%20short%20hair&width=200&height=200&seq=admin-cleaner-new-1&orientation=squarish",
  "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20friendly%20young%20man%20cleaner%20warm%20smile%20olive%20uniform%20clean%20cream%20background%20editorial%20natural%20soft%20light%20approachable&width=200&height=200&seq=admin-cleaner-new-2&orientation=squarish",
  "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20friendly%20woman%20cleaner%20warm%20smile%20sage%20apron%20clean%20cream%20background%20editorial%20natural%20soft%20light%20approachable%20curly%20hair&width=200&height=200&seq=admin-cleaner-new-3&orientation=squarish",
  "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20friendly%20man%20cleaner%20warm%20smile%20beige%20apron%20clean%20cream%20background%20editorial%20natural%20soft%20light%20approachable%20glasses&width=200&height=200&seq=admin-cleaner-new-4&orientation=squarish",
];

const initialForm = {
  name: "",
  role: roleOptions[0],
  city: cityOptions[0],
  status: "onboarding" as AdminCleaner["status"],
  image: placeholderImages[0],
};

export default function AddCleanerModal({ open, onClose, onAdd }: Props) {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(initialForm);
    setError(null);
    setUploadError(null);
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = form.name.trim();
    if (!trimmed) {
      setError("Please enter a name");
      return;
    }
    const id =
      trimmed.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") +
      "-" +
      Math.random().toString(36).slice(2, 6);
    onAdd({
      id,
      name: trimmed,
      role: form.role,
      city: form.city,
      status: form.status,
      image: form.image,
      jobs: 0,
      rating: 0,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-up">
        <div className="flex items-start justify-between gap-3 p-5 pb-3 sticky top-0 bg-background-50 border-b border-background-100">
          <div>
            <h4 className="font-heading text-lg text-foreground-950">
              Add a cleaner
            </h4>
            <p className="text-xs text-foreground-600 mt-0.5">
              Just the basics — you can fill in the rest later.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 pt-4 space-y-4">
          {/* Avatar picker */}
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-2">
              Photo
            </label>
            <div className="flex items-center gap-3">
              <div className="relative flex-shrink-0">
                <img
                  src={form.image}
                  alt="Selected"
                  className="w-16 h-16 rounded-full object-cover object-top border border-background-200"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Upload photo"
                  className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full bg-foreground-950 text-background-50 hover:bg-foreground-800 cursor-pointer"
                >
                  <i className="ri-camera-line text-xs"></i>
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (!file.type.startsWith("image/")) {
                    setUploadError("Please choose an image file");
                    return;
                  }
                  if (file.size > 5 * 1024 * 1024) {
                    setUploadError("Image must be under 5MB");
                    return;
                  }
                  const reader = new FileReader();
                  reader.onload = () => {
                    const result = reader.result;
                    if (typeof result === "string") {
                      setForm((f) => ({ ...f, image: result }));
                      setUploadError(null);
                    }
                  };
                  reader.onerror = () => setUploadError("Could not read that file");
                  reader.readAsDataURL(file);
                  e.target.value = "";
                }}
              />
              <div className="flex-1 min-w-0">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-800 text-xs font-medium cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-upload-2-line"></i>
                  Upload photo
                </button>
                <p className="text-[10px] text-foreground-500 mt-1.5">
                  PNG or JPG, up to 5MB. Or pick a preset below.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-3">
              {placeholderImages.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => {
                    setForm((f) => ({ ...f, image: src }));
                    setUploadError(null);
                  }}
                  aria-label={`Photo option ${i + 1}`}
                  className={`w-10 h-10 rounded-full overflow-hidden border-2 cursor-pointer transition-colors ${
                    form.image === src
                      ? "border-primary-500"
                      : "border-background-200 hover:border-background-300"
                  }`}
                >
                  <img
                    src={src}
                    alt=""
                    className="w-full h-full object-cover object-top"
                  />
                </button>
              ))}
            </div>
            {uploadError && (
              <p className="text-[11px] text-accent-700 mt-1.5 flex items-center gap-1">
                <i className="ri-information-line"></i>
                {uploadError}
              </p>
            )}
          </div>

          {/* Name */}
          <div>
            <label
              htmlFor="cleaner-name"
              className="block text-xs font-medium text-foreground-700 mb-1.5"
            >
              Full name
            </label>
            <input
              id="cleaner-name"
              type="text"
              value={form.name}
              onChange={(e) => {
                setForm((f) => ({ ...f, name: e.target.value }));
                if (error) setError(null);
              }}
              placeholder="e.g. Camila Reyes"
              className="w-full bg-background-100 text-sm text-foreground-900 placeholder:text-foreground-500 rounded-md px-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300"
              autoFocus
            />
            {error && (
              <p className="text-[11px] text-accent-700 mt-1.5 flex items-center gap-1">
                <i className="ri-information-line"></i>
                {error}
              </p>
            )}
          </div>

          {/* Role */}
          <div>
            <label
              htmlFor="cleaner-role"
              className="block text-xs font-medium text-foreground-700 mb-1.5"
            >
              Role
            </label>
            <select
              id="cleaner-role"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              className="w-full bg-background-100 text-sm text-foreground-900 rounded-md px-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* City */}
          <div>
            <label
              htmlFor="cleaner-city"
              className="block text-xs font-medium text-foreground-700 mb-1.5"
            >
              Service area
            </label>
            <select
              id="cleaner-city"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              className="w-full bg-background-100 text-sm text-foreground-900 rounded-md px-3 py-2.5 outline-none focus:bg-background-50 focus:ring-2 focus:ring-primary-300 cursor-pointer"
            >
              {cityOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div>
            <span className="block text-xs font-medium text-foreground-700 mb-1.5">
              Starting status
            </span>
            <div className="grid grid-cols-3 gap-2">
              {statusOptions.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, status: s.id }))}
                  className={`text-left px-3 py-2.5 rounded-md border text-xs cursor-pointer transition-colors ${
                    form.status === s.id
                      ? "border-primary-500 bg-primary-50 text-foreground-950"
                      : "border-background-200 bg-background-50 text-foreground-700 hover:border-background-300"
                  }`}
                >
                  <span className="block font-semibold">{s.label}</span>
                  <span className="block text-[10px] text-foreground-600 mt-0.5 leading-tight">
                    {s.hint}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm font-medium text-foreground-800 hover:bg-background-100 whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-primary-500 hover:bg-primary-600 text-background-50 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-user-add-line"></i>
              Add to roster
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}